import { Button } from 'antd';
import message from 'antd/lib/message';
import { observer } from 'mobx-react';
import React from 'react';
import styled from 'styled-components';

import { type IDynamicProperty } from 'stores/DynamicProperties/IDynamicProperty';
import store from 'stores/RootStore';

const PopoverButton = styled(Button)`
    width: 100%;
    margin-top: 5px;

    &:first-child {
        margin-top: 0;
    }
`;

export interface PropertyPopoverContentProps {
    property: IDynamicProperty<any>;
    editMode: boolean;
    onDismiss?: () => void;
}

function PropertyPopoverContent({
    property,
    editMode,
    onDismiss,
}: PropertyPopoverContentProps) {
    const creativeClipboard = store.creative?.clipboard;

    const handleCopy = () => {
        onDismiss?.();
        if (!creativeClipboard) return;
        try {
            creativeClipboard.copy(property);
            void message.success(
                <>
                    Property <b>{property.name}</b> copied to clipboard
                </>
            );
        } catch (e: any) {
            void message.error(
                <>
                    Failed to copy property <b>{property.name}</b>: {e.message}
                </>
            );
        }
    };

    const handlePaste = () => {
        onDismiss?.();
        if (!creativeClipboard) return;
        try {
            creativeClipboard.paste(property);
        } catch (e: any) {
            void message.error(
                <>
                    Failed to paste into property <b>{property.name}</b>:{' '}
                    {e.message}
                </>
            );
        }
    };

    const handleRemove = () => {
        onDismiss?.();
        try {
            property.parent?.remove(property.name);
        } catch (e: any) {
            void message.error(
                <>
                    Failed to remove property <b>{property.name}</b>:{' '}
                    {e.message}
                </>
            );
        }
    };

    const handleRevert = () => {
        onDismiss?.();
        property.revert();
    };

    const canCopy = !!creativeClipboard;
    const canPaste = creativeClipboard?.valuesCopied;
    const canRevert = property.getChanged();
    const canRemove = !!property.parent;

    return (
        <>
            <PopoverButton
                size="small"
                disabled={!canCopy}
                onClick={handleCopy}
            >
                Copy
            </PopoverButton>

            <PopoverButton
                size="small"
                disabled={!canPaste}
                onClick={handlePaste}
            >
                Paste
            </PopoverButton>

            <PopoverButton
                size="small"
                disabled={!canRevert}
                onClick={handleRevert}
            >
                Revert
            </PopoverButton>

            {editMode && (
                <PopoverButton
                    size="small"
                    disabled={!canRemove}
                    danger
                    onClick={handleRemove}
                >
                    Remove
                </PopoverButton>
            )}
        </>
    );
}

export default observer(PropertyPopoverContent);
